import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { ClipboardCheck, Loader2, Cpu, CheckCircle2, AlertTriangle, MinusCircle, XCircle, Eye, Users, Scale, Wand2, TrendingUp, MapPin, ShieldPlus, PiggyBank, TrendingDown, BadgeDollarSign } from "lucide-react";

const COV = {
  covered: { icon: CheckCircle2, cls: "text-emerald-500" },
  partial: { icon: MinusCircle, cls: "text-amber-500" },
  missing: { icon: XCircle, cls: "text-destructive" },
};

const fmt = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

function Gauge({ score }) {
  const color = score >= 75 ? "#10B981" : score >= 50 ? "#F59E0B" : "#EF4444";
  const r = 46, c = 2 * Math.PI * r, off = c - (score / 100) * c;
  return (
    <div className="relative h-32 w-32">
      <svg className="h-32 w-32 -rotate-90" viewBox="0 0 108 108">
        <circle cx="54" cy="54" r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="9" />
        <circle cx="54" cy="54" r={r} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset 0.7s ease" }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center"><div className="font-mono font-bold text-3xl tabular-nums" style={{ color }}>{score}</div><div className="text-[9px] font-mono uppercase text-muted-foreground">/ 100</div></div>
      </div>
    </div>
  );
}

export default function QuoteReviewTab({ projectId }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [applyingMode, setApplyingMode] = useState(null);
  const [savingBusy, setSavingBusy] = useState(false);
  const [applyingSave, setApplyingSave] = useState(false);
  const [view, setView] = useState("internal");
  const [region, setRegion] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["review", projectId], queryFn: async () => (await api.get(`/projects/${projectId}/quote-review`)).data });
  const { data: savings } = useQuery({ queryKey: ["savings", projectId], queryFn: async () => (await api.get(`/projects/${projectId}/savings`)).data });

  useEffect(() => { if (data?.region && !region) setRegion(data.region); }, [data]); // eslint-disable-line

  const run = async () => {
    setBusy(true);
    try { const { data: r } = await api.post(`/projects/${projectId}/quote-review`, { region }); qc.setQueryData(["review", projectId], r); toast.success("AI review complete"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const apply = async (mode = "improve") => {
    setApplyingMode(mode);
    try {
      const { data: r } = await api.post(`/projects/${projectId}/quote-review/apply`, { region, mode });
      qc.invalidateQueries({ queryKey: ["boq", projectId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast.success(`${mode === "harden" ? "Scope hardened" : "Estimate improved"} — new total ${fmt(r.grand_total)}, quality ${r.quality_score}. Re-run review to refresh the score.`);
    } catch (e) { toast.error(apiErr(e)); } finally { setApplyingMode(null); }
  };

  const applyBenchmark = async () => {
    setApplyingMode("benchmark");
    try {
      const { data: r } = await api.post(`/projects/${projectId}/quote-review/apply-benchmark`);
      qc.invalidateQueries({ queryKey: ["boq", projectId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      if (!r.updated) toast.info("No line items were above local market rates — nothing to lower.");
      else toast.success(`Lowered ${r.updated} item(s) to local market pricing — new total ${fmt(r.grand_total)}.`);
    } catch (e) { toast.error(apiErr(e)); } finally { setApplyingMode(null); }
  };

  const runSavings = async () => {
    setSavingBusy(true);
    try { const { data: r } = await api.post(`/projects/${projectId}/savings`); qc.setQueryData(["savings", projectId], r); toast.success("Savings analysis ready"); }
    catch (e) { toast.error(apiErr(e)); } finally { setSavingBusy(false); }
  };
  const applySavings = async () => {
    setApplyingSave(true);
    try {
      const { data: r } = await api.post(`/projects/${projectId}/savings/apply`);
      qc.invalidateQueries({ queryKey: ["boq", projectId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast.success(`Savings applied — ${fmt(r.saved)} off. New total ${fmt(r.grand_total)}.`);
    } catch (e) { toast.error(apiErr(e)); } finally { setApplyingSave(false); }
  };

  if (isLoading) return <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const hasReview = data && data.exists !== false && data.competitiveness_score !== undefined;
  const clientSafe = view === "client";
  const coverage = (data?.coverage || []).filter((c) => !clientSafe || c.status === "covered");
  const benchmark = data?.rate_benchmark || [];
  const improvements = data?.improvements || [];
  const vulnerabilities = data?.vulnerabilities || [];
  const hasSavings = savings && savings.exists !== false && savings.total_savings_pct !== undefined;
  const SEV = { high: "text-destructive border-destructive/40 bg-destructive/10", medium: "text-amber-500 border-amber-500/40 bg-amber-500/10", low: "text-sky-400 border-sky-500/40 bg-sky-500/10" };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-semibold flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-primary" /> AI Quote Review</h3>
        <div className="flex flex-wrap items-center gap-2">
          {hasReview && (
            <div className="flex rounded-lg border border-border overflow-hidden text-sm" data-testid="review-view-toggle">
              <button data-testid="view-internal" onClick={() => setView("internal")} className={`px-3 py-1.5 font-semibold inline-flex items-center gap-1.5 ${!clientSafe ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}><Eye className="h-3.5 w-3.5" />Internal</button>
              <button data-testid="view-client" onClick={() => setView("client")} className={`px-3 py-1.5 font-semibold inline-flex items-center gap-1.5 ${clientSafe ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}><Users className="h-3.5 w-3.5" />Client-safe</button>
            </div>
          )}
          <div className="inline-flex items-center gap-1.5 rounded-lg border border-input bg-popover px-2.5 py-1.5">
            <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
            <input data-testid="review-region-input" value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Region (e.g. Austin, TX)" className="bg-transparent text-sm outline-none w-40" />
          </div>
          <button data-testid="run-review-btn" onClick={run} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 font-bold hover:bg-orange-600 transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Cpu className="h-4 w-4" />} {hasReview ? "Re-run" : "Run AI Review"}
          </button>
        </div>
      </div>

      {!hasReview ? (
        <div className="rounded-xl border border-dashed border-border py-16 text-center">
          <ClipboardCheck className="h-9 w-9 mx-auto text-muted-foreground mb-3" />
          <p className="font-semibold">No review yet</p>
          <p className="text-sm text-muted-foreground">Set your market region, then run an AI review to score competitiveness, benchmark rates and get improvement suggestions.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="rounded-xl border border-border bg-card p-5 flex flex-col items-center text-center" data-testid="review-score-card">
            <Gauge score={data.competitiveness_score} />
            <div className="mt-3 font-semibold capitalize">{data.verdict}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Competitiveness</div>
            {!clientSafe && data.projected_score > data.competitiveness_score && (
              <div className="mt-2 text-xs font-mono text-emerald-500 inline-flex items-center gap-1" data-testid="projected-score">
                <TrendingUp className="h-3.5 w-3.5" /> Projected {data.projected_score} after improvements
              </div>
            )}
          </div>

          <div className="lg:col-span-2 space-y-5">
            <div className="rounded-xl border border-border bg-card p-5">
              <h4 className="font-semibold mb-2">{clientSafe ? "Summary" : "Internal Assessment"}</h4>
              <p className="text-sm text-muted-foreground leading-relaxed" data-testid="review-summary">{clientSafe ? data.client_summary : data.internal_summary}</p>
            </div>

            <div className="rounded-xl border border-border bg-card p-5">
              <h4 className="font-semibold mb-3">Scope Coverage</h4>
              <div className="grid sm:grid-cols-2 gap-2">
                {coverage.map((c, i) => {
                  const m = COV[c.status] || COV.partial; const Icon = m.icon;
                  return <div key={i} className="flex items-center gap-2 text-sm" data-testid={`coverage-${i}`}><Icon className={`h-4 w-4 shrink-0 ${m.cls}`} /><span>{c.area}</span></div>;
                })}
              </div>
            </div>

            {!clientSafe && vulnerabilities.length > 0 && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-5" data-testid="review-vulnerabilities">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h4 className="font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> Scope-Gap & Change-Order Vulnerabilities</h4>
                  <button data-testid="harden-scope-btn" onClick={() => apply("harden")} disabled={!!applyingMode}
                    className="inline-flex items-center gap-2 rounded-lg bg-amber-500 text-white px-4 py-2 font-bold hover:bg-amber-600 transition-colors disabled:opacity-60 text-sm">
                    {applyingMode === "harden" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldPlus className="h-4 w-4" />} Harden Scope
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground mb-3">Flags major CSI divisions that are absent or only represented by allowances, and anything likely to trigger a change order. "Harden Scope" replaces allowances with detailed line items and adds missing divisions.</p>
                <div className="space-y-2.5">
                  {vulnerabilities.map((v, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm" data-testid={`vulnerability-${i}`}>
                      <span className={`shrink-0 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${SEV[v.severity] || SEV.medium}`}>{v.severity || "med"}</span>
                      <div><span className="font-medium">{v.title}</span>{v.detail && <div className="text-xs text-muted-foreground">{v.detail}</div>}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!clientSafe && benchmark.length > 0 && (
              <div className="rounded-xl border border-border bg-card p-5" data-testid="rate-benchmark">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                  <h4 className="font-semibold flex items-center gap-2"><Scale className="h-4 w-4 text-primary" /> Rate Benchmarking</h4>
                  <button data-testid="apply-benchmark-btn" onClick={applyBenchmark} disabled={!!applyingMode}
                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 text-white px-3 py-2 font-bold hover:bg-emerald-600 transition-colors disabled:opacity-60 text-sm">
                    {applyingMode === "benchmark" ? <Loader2 className="h-4 w-4 animate-spin" /> : <BadgeDollarSign className="h-4 w-4" />} Find &amp; apply better pricing
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mb-3">Your unit rates vs US national and local {region ? `(${region})` : ""} market rates. "Find &amp; apply better pricing" lowers any line priced above the local market down to the benchmarked local rate.</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[560px]">
                    <thead><tr className="text-left text-[10px] font-mono uppercase tracking-wider text-muted-foreground border-b border-border/60">
                      <th className="px-2 py-2">Item</th><th className="px-2 py-2">Unit</th>
                      <th className="px-2 py-2 text-right">Yours</th><th className="px-2 py-2 text-right">US</th>
                      <th className="px-2 py-2 text-right">Local</th><th className="px-2 py-2 text-right">Var</th>
                    </tr></thead>
                    <tbody>
                      {benchmark.map((b, i) => {
                        const v = Number(b.variance_pct) || 0;
                        const vcls = v > 3 ? "text-destructive" : v < -3 ? "text-emerald-500" : "text-muted-foreground";
                        return (
                          <tr key={i} className="border-b border-border/40" data-testid={`benchmark-${i}`}>
                            <td className="px-2 py-2 font-medium">{b.item}<div className="text-[11px] text-muted-foreground font-normal">{b.note}</div></td>
                            <td className="px-2 py-2 font-mono text-xs">{b.unit}</td>
                            <td className="px-2 py-2 text-right font-mono tabular-nums">{fmt(b.your_rate)}</td>
                            <td className="px-2 py-2 text-right font-mono tabular-nums text-muted-foreground">{fmt(b.us_rate)}</td>
                            <td className="px-2 py-2 text-right font-mono tabular-nums text-muted-foreground">{fmt(b.local_rate)}</td>
                            <td className={`px-2 py-2 text-right font-mono tabular-nums font-semibold ${vcls}`}>{v > 0 ? "+" : ""}{v}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!clientSafe && improvements.length > 0 && (
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-5" data-testid="review-improvements">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h4 className="font-semibold flex items-center gap-2"><Wand2 className="h-4 w-4 text-primary" /> AI Improvements</h4>
                  <button data-testid="apply-improvements-btn" onClick={() => apply("improve")} disabled={!!applyingMode}
                    className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 font-bold hover:bg-orange-600 transition-colors disabled:opacity-60 text-sm">
                    {applyingMode === "improve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Apply to Estimate
                  </button>
                </div>
                <div className="space-y-3">
                  {improvements.map((im, i) => (
                    <div key={i} className="text-sm" data-testid={`improvement-${i}`}>
                      <div className="font-medium">{im.title}</div>
                      {im.detail && <div className="text-xs text-muted-foreground">{im.detail}</div>}
                      {im.impact && <div className="text-[11px] text-emerald-500 mt-0.5">Impact: {im.impact}</div>}
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-3">Applying rewrites the estimate line items, rates and markups. Re-run the review afterwards to see the updated score.</p>
              </div>
            )}

            {!clientSafe && (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5" data-testid="savings-card">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h4 className="font-semibold flex items-center gap-2"><PiggyBank className="h-4 w-4 text-emerald-500" /> Find Ways to Save</h4>
                  <div className="flex gap-2">
                    <button data-testid="find-savings-btn" onClick={runSavings} disabled={savingBusy}
                      className="inline-flex items-center gap-2 rounded-lg border border-emerald-500/50 text-emerald-500 px-3 py-2 font-bold hover:bg-emerald-500/10 transition-colors disabled:opacity-60 text-sm">
                      {savingBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingDown className="h-4 w-4" />} {hasSavings ? "Re-analyze" : "Find savings"}
                    </button>
                    {hasSavings && (
                      <button data-testid="apply-savings-btn" onClick={applySavings} disabled={applyingSave}
                        className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 text-white px-4 py-2 font-bold hover:bg-emerald-600 transition-colors disabled:opacity-60 text-sm">
                        {applyingSave ? <Loader2 className="h-4 w-4 animate-spin" /> : <BadgeDollarSign className="h-4 w-4" />} Apply savings
                      </button>
                    )}
                  </div>
                </div>
                {!hasSavings ? (
                  <p className="text-[11px] text-muted-foreground">Find value-engineering savings (bulk/trade pricing, alternate materials, crew efficiency, off-season rentals). "Apply savings" reduces your labor/material/equipment rates by the achievable % and recomputes the total.</p>
                ) : (
                  <>
                    {savings.summary && <p className="text-sm text-muted-foreground mb-3">{savings.summary}</p>}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                      {[["Overall", savings.total_savings_pct], ["Labor", savings.labor_pct], ["Material", savings.material_pct], ["Equipment", savings.equipment_pct]].map(([l, v]) => (
                        <div key={l} className="rounded-lg border border-emerald-500/30 bg-card px-3 py-2 text-center" data-testid={`savings-pct-${l.toLowerCase()}`}>
                          <div className="font-mono font-bold text-lg text-emerald-500 tabular-nums">-{v || 0}%</div>
                          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{l}</div>
                        </div>
                      ))}
                    </div>
                    <div className="space-y-2">
                      {(savings.opportunities || []).map((o, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm" data-testid={`savings-opp-${i}`}>
                          <span className="shrink-0 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border border-emerald-500/40 text-emerald-500">{o.category || "Gen"}</span>
                          <div><span className="font-medium">{o.area}</span>{o.action && <span className="text-muted-foreground">: {o.action}</span>}{o.saving && <span className="text-emerald-500 font-mono text-xs"> · {o.saving}</span>}</div>
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-3">Applying scales each cost rate by its savings %. Re-run the review afterwards to refresh the score.</p>
                  </>
                )}
              </div>
            )}

            {!clientSafe && (
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="rounded-xl border border-border bg-card p-5" data-testid="review-gaps">
                  <h4 className="font-semibold mb-3 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" />Scope Gaps</h4>
                  {(data.gaps || []).length === 0 ? <p className="text-sm text-emerald-500">None flagged.</p> : (
                    <ul className="space-y-1.5 text-sm text-muted-foreground list-disc pl-4">{data.gaps.map((g, i) => <li key={i}>{g}</li>)}</ul>
                  )}
                </div>
                <div className="rounded-xl border border-border bg-card p-5" data-testid="review-risks">
                  <h4 className="font-semibold mb-3 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-destructive" />Commercial Risks</h4>
                  {(data.risks || []).length === 0 ? <p className="text-sm text-emerald-500">None flagged.</p> : (
                    <ul className="space-y-1.5 text-sm text-muted-foreground list-disc pl-4">{data.risks.map((r, i) => <li key={i}>{r}</li>)}</ul>
                  )}
                </div>
              </div>
            )}
            {clientSafe && <p className="text-xs text-muted-foreground">Client-safe view hides internal rate benchmarks, gaps and commercial risk notes.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
